import { useQuery } from '@tanstack/react-query';
import { api } from '@/utils/Api/POS';
import { applyLogoToEmailTemplate } from '@/data/Marketing/DefaultTemplates';

/** Identifier of the marketing template row served by POST /api/communication/templates */
export const MARKETING_EMAIL_TEMPLATE_IDENTIFIER = 'MARKETING_TRIGGER_AND_CAMPAIGN';

interface UseMarketingEmailTemplateOptions {
    /** Absolute URL of the outlet logo, injected into the template placeholder */
    logoUrl?: string;
    enabled?: boolean;
}

interface UseMarketingEmailTemplateResult {
    /** Default email HTML, empty while the template is still being fetched */
    template: string;
    isLoading: boolean;
}

/**
 * Default email HTML for marketing campaigns and triggers.
 * The outlet-specific row wins over the global default row; when the API has no row,
 * the template is empty and callers leave the editor content untouched.
 */
export const useMarketingEmailTemplate = (
    options: UseMarketingEmailTemplateOptions = {},
): UseMarketingEmailTemplateResult => {
    const { logoUrl, enabled = true } = options;

    const query = useQuery({
        queryKey: ['communication-templates', MARKETING_EMAIL_TEMPLATE_IDENTIFIER, 'EMAIL'],
        queryFn: () =>
            api.postApiCommunicationTemplates({
                identifiers: [MARKETING_EMAIL_TEMPLATE_IDENTIFIER],
                modes: ['EMAIL'],
            }),
        staleTime: 5 * 60 * 1000,
        enabled,
        retry: 1,
    });

    if (!enabled) {
        return { template: '', isLoading: false };
    }

    if (query.isLoading) {
        return { template: '', isLoading: true };
    }

    const modeTemplates = query.data?.data?.templates?.[MARKETING_EMAIL_TEMPLATE_IDENTIFIER]?.EMAIL;
    // The outlet row is typed as an open record by the generated model, so narrow it before use
    const outletTemplate = modeTemplates?.outlet?.template;
    const apiTemplate =
        (typeof outletTemplate === 'string' ? outletTemplate : '') || modeTemplates?.default?.template || '';

    return {
        template: apiTemplate ? applyLogoToEmailTemplate(apiTemplate, logoUrl) : '',
        isLoading: false,
    };
};
